import React from 'react';

const ManageParticipantsSection = ({
  competitions,
  selectedCompetitionId,
  setSelectedCompetitionId,
  selectedPlayerIds,
  setSelectedPlayerIds,
  selectedClubIds,
  setSelectedClubIds,
  selectedCoachIds,
  setSelectedCoachIds,
  selectedLifestyleIds,
  setSelectedLifestyleIds,
  selectedArtistIds,          // NEW: Add artist IDs
  setSelectedArtistIds,       // NEW: Set artist IDs
  selectedSongIds,            // NEW: Add song IDs
  setSelectedSongIds,         // NEW: Set song IDs
  players,
  clubs,
  coaches,
  lifestyles,
  artists,                    // NEW: Add artists data
  songs,                      // NEW: Add songs data
  isAddingParticipants,
  handleAddParticipantsToCompetition,
  handleRemoveParticipant
}) => {
  const selectedCompetition = competitions.find(c => c.id === selectedCompetitionId);

  const renderParticipantSelector = () => {
    if (!selectedCompetition) return null;

    if (selectedCompetition.status !== 'draft') {
      return (
        <div className="text-accent-soft text-sm mt-4">
          Cannot add participants - competition is no longer in draft status
        </div>
      );
    }

    switch (selectedCompetition.category) {
      case 'player':
        return (
          <div className="mt-4">
            <h3 className="text-lg font-medium mb-2">Select Players to Add</h3>
            <div className="flex gap-2">
              <select
                multiple
                value={selectedPlayerIds}
                onChange={(e) => {
                  const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                  setSelectedPlayerIds(selectedOptions);
                }}
                className="field min-h-[200px]"
              >
                {players.map(player => (
                  <option key={player.playerId} value={player.playerId}>
                    {player.name || `${player.firstName} ${player.lastName}`}
                  </option>
                ))}
              </select>
              <div className="flex flex-col justify-center">
                <button
                  onClick={() => handleAddParticipantsToCompetition(selectedCompetitionId, 'player', selectedPlayerIds)}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedPlayerIds.length === 0 || isAddingParticipants}
                >
                  {isAddingParticipants ? 'Adding...' : 'Add Selected Players'}
                </button>
                <div className="mt-2 text-sm text-gray-400">
                  {selectedPlayerIds.length} player(s) selected
                </div>
              </div>
            </div>
          </div>
        );

      case 'club':
        return (
          <div className="mt-4">
            <h3 className="text-lg font-medium mb-2">Select Clubs to Add</h3>
            <div className="flex gap-2">
              <select
                multiple
                value={selectedClubIds}
                onChange={(e) => {
                  const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                  setSelectedClubIds(selectedOptions);
                }}
                className="field min-h-[200px]"
              >
                {clubs.map(club => (
                  <option key={club.clubId} value={club.clubId}>
                    {club.name}
                  </option>
                ))}
              </select>
              <div className="flex flex-col justify-center">
                <button
                  onClick={() => handleAddParticipantsToCompetition(selectedCompetitionId, 'club', selectedClubIds)}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedClubIds.length === 0 || isAddingParticipants}
                >
                  {isAddingParticipants ? 'Adding...' : 'Add Selected Clubs'}
                </button>
                <div className="mt-2 text-sm text-gray-400">
                  {selectedClubIds.length} club(s) selected
                </div>
              </div>
            </div>
          </div>
        );

      case 'coach':
        return (
          <div className="mt-4">
            <h3 className="text-lg font-medium mb-2">Select Coaches to Add</h3>
            <div className="flex gap-2">
              <select
                multiple
                value={selectedCoachIds}
                onChange={(e) => {
                  const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                  setSelectedCoachIds(selectedOptions);
                }}
                className="field min-h-[200px]"
              >
                {coaches.map(coach => (
                  <option key={coach.id} value={coach.id}>
                    {coach.name || `${coach.firstName} ${coach.lastName}`}
                  </option>
                ))}
              </select>
              <div className="flex flex-col justify-center">
                <button
                  onClick={() => handleAddParticipantsToCompetition(selectedCompetitionId, 'coach', selectedCoachIds)}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedCoachIds.length === 0 || isAddingParticipants}
                >
                  {isAddingParticipants ? 'Adding...' : 'Add Selected Coaches'}
                </button>
                <div className="mt-2 text-sm text-gray-400">
                  {selectedCoachIds.length} coach(es) selected
                </div>
              </div>
            </div>
          </div>
        );

      case 'model':
        return (
          <div className="mt-4">
            <h3 className="text-lg font-medium mb-2">Select Lifestyle Models to Add</h3>
            <div className="flex gap-2">
              <select
                multiple
                value={selectedLifestyleIds}
                onChange={(e) => {
                  const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                  setSelectedLifestyleIds(selectedOptions);
                }}
                className="field min-h-[200px]"
              >
                {lifestyles.map(model => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
              <div className="flex flex-col justify-center">
                <button
                  onClick={() => handleAddParticipantsToCompetition(selectedCompetitionId, 'model', selectedLifestyleIds)}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedLifestyleIds.length === 0 || isAddingParticipants}
                >
                  {isAddingParticipants ? 'Adding...' : 'Add Selected Models'}
                </button>
                <div className="mt-2 text-sm text-gray-400">
                  {selectedLifestyleIds.length} model(s) selected
                </div>
              </div>
            </div>
          </div>
        );

      case 'artist': // NEW: Handle artist competitions
        return (
          <div className="mt-4">
            <h3 className="text-lg font-medium mb-2">Select Artists to Add</h3>
            <div className="flex gap-2">
              <select
                multiple
                value={selectedArtistIds}
                onChange={(e) => {
                  const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                  setSelectedArtistIds(selectedOptions);
                }}
                className="field min-h-[200px]"
              >
                {artists.map(artist => (
                  <option key={artist.artist_id} value={artist.artist_id}>
                    {artist.name} {artist.country ? `(${artist.country})` : ''}
                  </option>
                ))}
              </select>
              <div className="flex flex-col justify-center">
                <button
                  onClick={() => handleAddParticipantsToCompetition(selectedCompetitionId, 'artist', selectedArtistIds)}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedArtistIds.length === 0 || isAddingParticipants}
                >
                  {isAddingParticipants ? 'Adding...' : 'Add Selected Artists'}
                </button>
                <div className="mt-2 text-sm text-gray-400">
                  {selectedArtistIds.length} artist(s) selected
                </div>
              </div>
            </div>
          </div>
        );

      case 'song': // NEW: Handle song competitions
        return (
          <div className="mt-4">
            <h3 className="text-lg font-medium mb-2">Select Songs to Add</h3>
            <div className="flex gap-2">
              <select
                multiple
                value={selectedSongIds}
                onChange={(e) => {
                  const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                  setSelectedSongIds(selectedOptions);
                }}
                className="field min-h-[200px]"
              >
                {songs.map(song => {
                  // Try to get artist name if available
                  const artistName = song.Artist?.name || song.artist?.name || '';
                  return (
                    <option key={song.song_id} value={song.song_id}>
                      {song.title} {artistName ? `- ${artistName}` : ''} {song.genre ? `[${song.genre}]` : ''}
                    </option>
                  );
                })}
              </select>
              <div className="flex flex-col justify-center">
                <button
                  onClick={() => handleAddParticipantsToCompetition(selectedCompetitionId, 'song', selectedSongIds)}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedSongIds.length === 0 || isAddingParticipants}
                >
                  {isAddingParticipants ? 'Adding...' : 'Add Selected Songs'}
                </button>
                <div className="mt-2 text-sm text-gray-400">
                  {selectedSongIds.length} song(s) selected
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return <div className="mt-4 text-yellow-400">This competition category doesn't support participants</div>;
    }
  };

  const getParticipantName = (participant) => {
    if (!participant) return 'Unknown';
    
    // If we have details, use them
    if (participant.details) {
      switch (selectedCompetition?.category) {
        case 'player':
          return participant.details.name || 'Player';
        case 'club':
          return participant.details.name || 'Club';
        case 'coach':
          return participant.details.firstName && participant.details.lastName ? 
            `${participant.details.firstName} ${participant.details.lastName}` : 
            participant.details.name || 'Coach';
        case 'model':
          return participant.details.name || 'Model';
        case 'artist':
          return participant.details.name || 'Artist';
        case 'song':
          // For songs, show title and artist if available
          if (participant.details.title) {
            const artistName = participant.details.Artist?.name || participant.details.artist?.name;
            return artistName ? `${participant.details.title} - ${artistName}` : participant.details.title;
          }
          return participant.details.name || 'Song';
        default:
          return participant.name || 'Participant';
      }
    }
    
    // Fallback to direct properties
    return participant.name || 'Participant';
  };

  const getParticipantImage = (participant) => {
    if (!participant || !participant.details) return null;
    
    switch (selectedCompetition?.category) {
      case 'player':
        return participant.details.profileImageUrl;
      case 'club':
        return participant.details.logo;
      case 'coach':
        return participant.details.profilePicture;
      case 'model':
        return participant.details.profileImageUrl;
      case 'artist':
        // Artists might not have an image field yet
        return null;
      case 'song':
        return participant.details.cover_image || participant.details.coverImage;
      default:
        return null;
    }
  };

  const getParticipantAltText = (participant) => {
    if (!participant || !participant.details) return 'Participant';
    
    switch (selectedCompetition?.category) {
      case 'player':
        return participant.details.name || 'Player';
      case 'club':
        return participant.details.name || 'Club';
      case 'coach':
        return participant.details.firstName && participant.details.lastName ? 
          `${participant.details.firstName} ${participant.details.lastName}` : 
          participant.details.name || 'Coach';
      case 'model':
        return participant.details.name || 'Model';
      case 'artist':
        return participant.details.name || 'Artist';
      case 'song':
        return participant.details.title || 'Song';
      default:
        return 'Participant';
    }
  };

  const getParticipantSubText = (participant) => {
    if (!participant || !participant.details) return null;
    
    switch (selectedCompetition?.category) {
      case 'artist':
        return participant.details.country || participant.details.bio?.substring(0, 30);
      case 'song':
        const artistName = participant.details.Artist?.name || participant.details.artist?.name;
        const genre = participant.details.genre;
        if (artistName && genre) return `${artistName} • ${genre}`;
        if (artistName) return artistName;
        if (genre) return genre;
        return null;
      default:
        return null;
    }
  };

  return (
    <div className="card p-5">
      <h2 className="text-2xl font-bold mb-6">Manage Participants</h2>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">Select Competition</label>
          <select
            value={selectedCompetitionId || ''}
            onChange={(e) => {
              setSelectedCompetitionId(Number(e.target.value));
              setSelectedPlayerIds([]);
              setSelectedClubIds([]);
              setSelectedCoachIds([]);
              setSelectedLifestyleIds([]);
              setSelectedArtistIds([]);  // Clear artist selections
              setSelectedSongIds([]);     // Clear song selections
            }}
            className="field"
          >
            <option value="">Choose a competition...</option>
            {competitions.map(comp => (
              <option key={comp.id} value={comp.id}>
                {comp.title} ({comp.category === 'model' ? 'Lifestyle Model' : 
                              comp.category === 'artist' ? 'Artist' :
                              comp.category === 'song' ? 'Song' : 
                              comp.category})
              </option>
            ))}
          </select>
        </div>

        {selectedCompetitionId && selectedCompetition && (
          <div className="space-y-6">
            <div className="panel p-4">
              <h3 className="text-lg font-medium mb-2">Competition Details</h3>
              <p><span className="font-medium">Title:</span> {selectedCompetition.title}</p>
              <p><span className="font-medium">Category:</span> {
                selectedCompetition.category === 'model' ? 'Lifestyle Model' : 
                selectedCompetition.category === 'artist' ? 'Artist' :
                selectedCompetition.category === 'song' ? 'Song' :
                selectedCompetition.category.charAt(0).toUpperCase() + selectedCompetition.category.slice(1)
              }</p>
              <p><span className="font-medium">Status:</span> {selectedCompetition.status}</p>
            </div>

            {renderParticipantSelector()}

            <div className="mt-6">
              <h3 className="text-lg font-medium mb-2">Current Participants</h3>
              <div className="space-y-2">
                {selectedCompetition?.participants?.length > 0 ? (
                  selectedCompetition.participants.map((participant, index) => {
                    const imageUrl = getParticipantImage(participant);
                    const name = getParticipantName(participant);
                    const altText = getParticipantAltText(participant);
                    const subText = getParticipantSubText(participant);

                    return (
                      <div 
                        key={`participant-${participant.id}-${index}`} 
                        className="flex justify-between items-center bg-surface-900 border border-line p-3 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          {imageUrl && (
                            <img 
                              src={imageUrl} 
                              alt={altText}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          )}
                          <div className="flex flex-col">
                            <span className="font-medium">{name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-400 capitalize">
                                {selectedCompetition.category === 'model' ? 'Lifestyle Model' : 
                                 selectedCompetition.category === 'artist' ? 'Artist' :
                                 selectedCompetition.category === 'song' ? 'Song' :
                                 selectedCompetition.category}
                              </span>
                              {subText && (
                                <>
                                  <span className="text-gray-500">•</span>
                                  <span className="text-sm text-gray-400">{subText}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        {selectedCompetition.status === 'draft' && (
                          <button
                            onClick={() => handleRemoveParticipant(selectedCompetition.id, participant.id)}
                            className="px-3 py-1 bg-red-600 hover:bg-red-500 text-white rounded-md text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-gray-400 p-4 text-center border border-gray-700 rounded-lg">
                    No participants added yet
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageParticipantsSection;